import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import i18n from '../../i18n/index';
import { getPost, BLOG_POSTS } from './data';

// Renders a single blog post at /blog/:slug. Mirrors SeoHeadshotPage styling
// and drives the reader toward signup.
const BlogPost = () => {
  const { slug } = useParams();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const post = getPost(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentLanguage(i18n.language || 'en');
    if (post && post.title) {
      document.title = `${post.title} | HeadshotsNow.ai`;
    }
  }, [slug, post]);

  const blogHref = currentLanguage === 'en' ? '/blog' : `/${currentLanguage}/blog`;
  const signupHref = currentLanguage === 'en' ? '/signup' : `/${currentLanguage}/signup`;

  if (!post) {
    return (
      <main className="bg-black text-white">
        <section className="section-top-80 px-[15px]">
          <div className="mx-auto max-w-[700px] text-center pt-24 pb-16">
            <h1 className="text-3xl font-bold mb-4">Post not found</h1>
            <p className="text-white/70 mb-8">That article doesn’t exist or has moved.</p>
            <Link
              to={blogHref}
              className="inline-block bg-cyan-600 hover:bg-cyan-700 transition-colors text-white font-semibold rounded-full px-8 py-3"
            >
              Back to the blog
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <main className="bg-black text-white">
      {/* Hero */}
      <article className="px-[15px]">
        <div className="section-top-80 mx-auto max-w-[800px] pt-16 pb-6">
          <Link to={blogHref} className="text-sm text-cyan-400 hover:text-cyan-300">
            ← All articles
          </Link>
          <span className="mt-6 block text-xs font-semibold tracking-wide uppercase text-cyan-400">
            From the HeadshotsNow blog
          </span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold leading-tight">{post.title}</h1>
          <p className="mt-3 text-sm text-white/50">
            {post.date} · {post.readTime}
          </p>
          <p className="mt-6 text-lg text-white/80">{post.intro}</p>

          {/* Inline CTA */}
          <div className="mt-8">
            <Link
              to={signupHref}
              className="inline-block bg-cyan-600 hover:bg-cyan-700 transition-colors text-white font-semibold rounded-full px-8 py-3"
            >
              Get your AI headshots
            </Link>
          </div>
        </div>

        {/* Body */}
        <div className="mx-auto max-w-[800px] space-y-10 py-6">
          {post.sections.map((sec, idx) => (
            <section key={idx}>
              <h2 className="text-2xl font-bold mb-4">{sec.heading}</h2>
              {sec.paragraphs &&
                sec.paragraphs.map((p, i) => (
                  <p key={i} className="text-white/80 mb-4 leading-relaxed">
                    {p}
                  </p>
                ))}
              {sec.list && (
                <ul className="list-disc list-inside space-y-2 text-white/80">
                  {sec.list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* FAQ */}
          {post.faq && post.faq.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold mb-4">Frequently asked questions</h2>
              <div className="space-y-5">
                {post.faq.map((item, i) => (
                  <div key={i}>
                    <h3 className="font-semibold text-white mb-1">{item.q}</h3>
                    <p className="text-white/75">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      {/* Closing CTA */}
      <section className="px-[15px] py-12">
        <div className="mx-auto max-w-[800px] rounded-3xl bg-neutral-900 p-10 text-center">
          <h2 className="text-2xl font-bold mb-3">Ready for your own professional headshots?</h2>
          <p className="text-white/70 mb-6 max-w-[560px] mx-auto">
            Upload a few selfies, choose your styles, and get a batch of polished headshots — delivered
            online in about an hour.
          </p>
          <Link
            to={signupHref}
            className="inline-block bg-cyan-600 hover:bg-cyan-700 transition-colors text-white font-semibold rounded-full px-8 py-3"
          >
            Create my headshots
          </Link>
          <p className="text-xs text-white/50 mt-4">
            Results are AI-generated and may vary with the quality of your uploaded photos.
          </p>
        </div>
      </section>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="px-[15px] pb-16">
          <div className="mx-auto max-w-[1000px]">
            <h2 className="text-xl font-bold mb-6">Keep reading</h2>
            <div className="grid gap-6 sm:grid-cols-3">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={currentLanguage === 'en' ? `/blog/${p.slug}` : `/${currentLanguage}/blog/${p.slug}`}
                  className="block bg-neutral-900 rounded-2xl p-6 transition-colors hover:bg-neutral-800"
                >
                  <h3 className="font-semibold mb-2">{p.title}</h3>
                  <p className="text-sm text-white/60">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default BlogPost;
